import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CreditCard, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/cn';

// Give THIS passenger a compensation card, for an amount the operator chooses.
//
// This is the discretionary path: it does not wait for the rebooking engine to
// decide a meal voucher, so nothing but the operator's judgement stands behind
// the spend — which is exactly why a reason is mandatory and why the amount is
// typed, never implied. The entitlement path (engine-decided) needs no dialog.

// The amount chips come FROM POLICY (the care board's `knobs.topUpPresets`, the same
// source «Recargar» reads — #626). These are only the fallback for a case with no care
// board (standalone) or a board that predates the field. Both tables hold KEYS, never
// language — the dialog renders them with `t()`.
const DEFAULT_PRESETS: ReadonlyArray<{ labelKey: string; amount: number }> = [
  { labelKey: 'issueDialog.presets.breakfast', amount: 15 },
  { labelKey: 'issueDialog.presets.lunch', amount: 25 },
  { labelKey: 'issueDialog.presets.dinner', amount: 30 },
  { labelKey: 'issueDialog.presets.overnightWait', amount: 40 },
];

const REASON_CODES = [
  'AIRPORT_WAIT_MEAL',
  'DELAY_EXTENSION',
  'SPECIAL_NEED',
  'ACCESSIBILITY',
  'GOODWILL',
  'ERROR_CORRECTION',
] as const;

export interface IssueCardSubmit {
  amountUsd: number;
  reason: string;
}

export function IssueCardDialog({
  open,
  pnrLabel,
  paxCount,
  presets: policyPresets,
  pending,
  error,
  onOpenChange,
  onConfirm,
}: {
  open: boolean;
  pnrLabel: string;
  paxCount?: number | undefined;
  /** The policy's top-up presets (care board knobs); empty or absent = local defaults. */
  presets?: ReadonlyArray<{ label: string; amount: number }> | undefined;
  pending: boolean;
  error?: string | null;
  onOpenChange: (open: boolean) => void;
  onConfirm: (input: IssueCardSubmit) => void;
}) {
  const { t } = useTranslation('admin');
  const [amountRaw, setAmountRaw] = useState('');
  const [reasonCode, setReasonCode] = useState<string>('');
  const [note, setNote] = useState('');

  const presets = policyPresets?.length
    ? policyPresets
    : DEFAULT_PRESETS.map((p) => ({ label: t(p.labelKey), amount: p.amount }));

  const amount = Number(amountRaw);
  const amountValid = Number.isFinite(amount) && amount > 0;
  const canSubmit = amountValid && !!reasonCode && !pending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="z-[60] max-w-md">
        <DialogHeader>
          <DialogTitle>{t('issueDialog.title', { pnr: pnrLabel })}</DialogTitle>
          <DialogDescription>
            {t('issueDialog.descriptionBefore')}
            {paxCount && paxCount > 1 ? t('issueDialog.shared', { n: paxCount }) : ''}
            {t('issueDialog.descriptionAfter')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 text-sm">
          <div className="space-y-1.5">
            <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
              {t('issueDialog.amountLabel')}
            </span>
            <div className="flex flex-wrap gap-1.5">
              {presets.map((p, i) => (
                <button
                  key={`${i}-${p.amount}`}
                  type="button"
                  onClick={() => setAmountRaw(String(p.amount))}
                  className={cn(
                    'rounded-md border px-2.5 py-1 text-xs font-medium transition-colors',
                    Number(amountRaw) === p.amount
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border hover:bg-muted',
                  )}
                  data-testid={`issue-card-preset-${p.amount}`}
                >
                  {p.label} ${p.amount}
                </button>
              ))}
            </div>
            <input
              inputMode="decimal"
              className="w-full rounded-md border border-input bg-background px-2 py-1.5 tabular-nums"
              value={amountRaw}
              onChange={(e) => setAmountRaw(e.target.value)}
              placeholder="35"
              aria-label={t('issueDialog.amountAria')}
              data-testid="issue-card-amount"
            />
          </div>

          <label className="block space-y-1.5">
            <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
              {t('issueDialog.reason')}
            </span>
            <select
              className="w-full rounded-md border border-input bg-background px-2 py-1.5"
              value={reasonCode}
              onChange={(e) => setReasonCode(e.target.value)}
              data-testid="issue-card-reason"
            >
              <option value="">{t('issueDialog.chooseReason')}</option>
              {REASON_CODES.map((code) => (
                <option key={code} value={code}>
                  {t(`issueDialog.reasons.${code}`)}
                </option>
              ))}
            </select>
          </label>

          <label className="block space-y-1.5">
            <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
              {t('issueDialog.note')}
            </span>
            <input
              className="w-full rounded-md border border-input bg-background px-2 py-1.5"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t('issueDialog.notePlaceholder')}
              data-testid="issue-card-note"
            />
          </label>

          {error ? (
            <p
              className="rounded-md border border-destructive/30 bg-destructive/5 p-2 text-destructive"
              role="alert"
              data-testid="issue-card-error"
            >
              {error}
            </p>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t('common:common.cancel')}
          </Button>
          <Button
            onClick={() =>
              onConfirm({
                amountUsd: amount,
                reason: note.trim() ? `${reasonCode}: ${note.trim()}` : reasonCode,
              })
            }
            disabled={!canSubmit}
            data-testid="issue-card-confirm"
          >
            {pending ? (
              <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" aria-hidden />
            ) : (
              <CreditCard className="mr-1 h-3.5 w-3.5" aria-hidden />
            )}
            {t('issueDialog.submit', {
              amount: amountValid ? amount.toLocaleString() : '—',
            })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
